import type { CreateLineItemRequestPayload } from '@vy/api-definition';
import { buildEmptyRevenueBreakdown, buildLineItemRevenueBreakdown } from '@vy/services';
import {
  BILLING_INTERVALS,
  INTEGRATION_PROVIDERS,
  LINE_ITEM_TYPES,
  LINE_ITEM_WITH_NO_PRODUCT_TYPES,
  PAYMENT_TERMS,
  PRICING_MODELS,
  type LineItemWithNoProductType,
} from '@vy/types';
import { isNullOrUndefined, isNotNullOrUndefined, purge, raise, raiseUnreachable, validateNotEmptyString } from '@vy/utils';
import type { PropsWithChildren } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { QueryKey } from 'react-query';
import { useQueryClient } from 'react-query';
import { VyDialog } from '../../../../components/new-design/feedback/VyDialog';
import { useCreateLineItem } from '../../../../hooks/requests/use-create-revisioned-line-item';
import { useUpdateLineItem } from '../../../../hooks/requests/use-update-line-item';
import { getPrivateApi } from '../../../../services/api';
import { runMutation } from '../../../../utils/vy-mutation';
import type { RevisionedLineItemDialogFormType } from './revisioned-line-item-dialog-forms/revisioned-line-item-dialog-forms-types/revisioned-line-item-dialog-type';
import { mapFormValuesToIntegrationEntity } from '../../CustomerInvoicePage/customer-invoice-page-components/InvoiceActivity/invoice-activity-components/InvoiceActivityTabs/invoice-activity-tabs/InvoiceActivityErpSyncTab/Invoice-activity-erp-sync-tab-components/ErpProductSettingsAccordion/erp-product-settings-accordion-utils/erp-product-settings-accordion-form-utils';
import { useActivatedIntegration } from '../../../../hooks/use-activated-integration';

export const RevisionedLineItemDialogSubmitActions = {
  Create: 'Create',
  Update: 'Update',
} as const;

export type RevisionedLineItemDialogSubmitAction =
  (typeof RevisionedLineItemDialogSubmitActions)[keyof typeof RevisionedLineItemDialogSubmitActions];

type RevisionedLineItemDialogFormProps<FormSchema extends RevisionedLineItemDialogFormType> =
  PropsWithChildren<{
    lineItemId?: string;
    title?: string;
    open: boolean;
    onClose?: () => void;
    lineItemType: LineItemWithNoProductType;
    invoiceId: string;
    form: UseFormReturn<FormSchema>;
    isDisabled?: boolean;
    submitAction?: RevisionedLineItemDialogSubmitAction;
    submitButtonLabel?: string;
    queryKeyToInvalidate?: QueryKey;
  }>;

export const RevisionedLineItemDialogForm = <FormSchema extends RevisionedLineItemDialogFormType>({
  lineItemId,
  title,
  open,
  onClose,
  lineItemType,
  invoiceId,
  form,
  isDisabled = false,
  children,
  submitAction = RevisionedLineItemDialogSubmitActions.Create,
  submitButtonLabel = 'Create',
  queryKeyToInvalidate,
}: RevisionedLineItemDialogFormProps<FormSchema>) => {
  const queryClient = useQueryClient();
  const createFixedLineItemMutation = useCreateLineItem();
  const updateFixedLineItemMutation = useUpdateLineItem();
  const isNetSuiteEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.netSuite, { suspense: true });
  const isXeroEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.xero, { suspense: true });

  const onSubmit = form.handleSubmit(async (data) => {
    if (isNullOrUndefined(data)) {
      return undefined;
    }

    const xeroExternalId = isXeroEnabled ? data.xeroProductId : undefined;

    switch (submitAction) {
      case RevisionedLineItemDialogSubmitActions.Create:
        await runMutation(
          createFixedLineItemMutation,
          buildCreateLineItemInput(lineItemType, invoiceId, data, isNetSuiteEnabled),
          {
            pending: 'Creating line item...',
            success: 'Line item created',
            error: 'Failed to create line item',
          },
          {
            onSuccess: async (response) => {
              if (isNotNullOrUndefined(xeroExternalId) && isNotNullOrUndefined(response)) {
                const api = getPrivateApi();
                await api.modifyExternalProduct({
                  productId: response.updatedLineItem.id,
                  integration: INTEGRATION_PROVIDERS.xero,
                  externalId: xeroExternalId,
                });
              }
              await queryClient.invalidateQueries(queryKeyToInvalidate);
            },
          },
        );
        break;
      case RevisionedLineItemDialogSubmitActions.Update: {
        validateNotEmptyString(lineItemId, 'lineItemId');
        const isXeroProductDirty = 'xeroProductId' in form.formState.dirtyFields;
        await runMutation(
          updateFixedLineItemMutation,
          {
            lineItemId,
            updateLineItemRequest: {
              ...data,
              modifiedDescription: data.modifiedDescription ?? null,
              invoiceId,
              type: LINE_ITEM_TYPES.NonProductFixed,
            },
          },
          {
            pending: 'Updating line item...',
            success: 'Line item updated',
            error: 'Failed to update line item',
          },
          {
            onSuccess: async () => {
              if (isXeroEnabled && isXeroProductDirty) {
                const api = getPrivateApi();
                await api.modifyExternalProduct({
                  productId: lineItemId,
                  integration: INTEGRATION_PROVIDERS.xero,
                  externalId: xeroExternalId ?? undefined,
                });
              }
              await queryClient.invalidateQueries(queryKeyToInvalidate);
            },
          },
        );
        break;
      }
      default:
        raiseUnreachable(submitAction);
    }

    return handleClose();
  });

  const handleClose = () => {
    form.reset();
    form.clearErrors();
    onClose?.();
  };

  const ctaDisabled =
    isDisabled || createFixedLineItemMutation.isLoading || updateFixedLineItemMutation.isLoading;

  return (
    <VyDialog
      open={open}
      onClose={handleClose}
      label={title ?? ''}
      labelDescription="Changes and updates will apply to this invoice only, and will not affect future invoices."
      cancelButtonText="Cancel"
      ctaText={submitButtonLabel}
      ctaDisabled={ctaDisabled}
      onSubmit={onSubmit}
    >
      {children}
    </VyDialog>
  );
};

function buildCreateLineItemInput(
  lineItemType: LineItemWithNoProductType,
  invoiceId: string,
  data: RevisionedLineItemDialogFormType,
  isNetSuiteEnabled: boolean,
): CreateLineItemRequestPayload {
  const { netSuiteProduct, classMetadata } = data;

  const netSuiteIntegrationEntity = isNetSuiteEnabled
    ? mapFormValuesToIntegrationEntity({
        product: netSuiteProduct,
        classMetadata,
      })
    : undefined;

  const integrationEntities = purge([netSuiteIntegrationEntity]);

  if (lineItemType === LINE_ITEM_WITH_NO_PRODUCT_TYPES.Fixed && 'revenueBreakdown' in data) {
    return {
      type: LINE_ITEM_TYPES.NonProductFixed,
      lineItemProduct: {
        proRata: false,
        paymentTerm: PAYMENT_TERMS.Postpayment,
        billingInterval: BILLING_INTERVALS.Monthly,
        variants: [
          {
            name: data.modifiedName,
            description: data.modifiedDescription,
            pricing: {
              pricingModel: PRICING_MODELS.FixedOneTime,
              priceConfiguration: {
                shouldCreateSeparateInvoice: false,
                price: data.revenueBreakdown.total,
              },
            },
          },
        ],
        integrationEntities,
      },
      modifiedDescription: data.modifiedDescription ?? null,
      modifiedName: data.modifiedName,
      isRevised: true,
      revenueBreakdown: data.revenueBreakdown,
      percentage: null,
      invoiceId,
    };
  }

  if (
    lineItemType === LINE_ITEM_WITH_NO_PRODUCT_TYPES.FixedPromotion &&
    'type' in data &&
    data.type === LINE_ITEM_WITH_NO_PRODUCT_TYPES.FixedPromotion
  ) {
    return {
      type: LINE_ITEM_TYPES.NonProductFixed,
      lineItemProduct: {
        proRata: false,
        paymentTerm: PAYMENT_TERMS.Postpayment,
        billingInterval: BILLING_INTERVALS.Monthly,
        integrationEntities,
        variants: [
          {
            name: data.modifiedName,
            description: data.modifiedDescription,
            pricing: {
              pricingModel: PRICING_MODELS.FixedDeduction,
              priceConfiguration: {
                amountToDeduct: data.revenueBreakdown.total,
              },
            },
          },
        ],
      },
      percentage: null,
      modifiedDescription: data.modifiedDescription ?? null,
      modifiedName: data.modifiedName,
      isRevised: true,
      revenueBreakdown: buildLineItemRevenueBreakdown(-data.revenueBreakdown.total, true),
      invoiceId,
    };
  }

  if (
    lineItemType === LINE_ITEM_WITH_NO_PRODUCT_TYPES.PercentagePromotion &&
    'type' in data &&
    data.type === LINE_ITEM_WITH_NO_PRODUCT_TYPES.PercentagePromotion
  ) {
    return {
      type: LINE_ITEM_TYPES.NonProductFixed,
      percentage: data.percentage,
      lineItemProduct: {
        proRata: false,
        paymentTerm: PAYMENT_TERMS.Postpayment,
        billingInterval: BILLING_INTERVALS.Monthly,
        integrationEntities,
        variants: [
          {
            name: data.modifiedName,
            description: data.modifiedDescription,
            pricing: {
              pricingModel: PRICING_MODELS.PercentageDeduction,
              priceConfiguration: {
                percentageToDeduct: data.percentage,
              },
            },
          },
        ],
      },
      modifiedDescription: data.modifiedDescription ?? null,
      modifiedName: data.modifiedName,
      isRevised: true,
      revenueBreakdown: buildEmptyRevenueBreakdown(),
      invoiceId,
    };
  }

  return raise('Unsupported line item type');
}
